import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import compact from 'lodash/compact';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import {
  TemplateFieldDTO,
} from 'dtos';
import {
  ToolbarItemProps,
} from 'views/EMR/components/TextAreaToolbar/index';
import FieldPopper, {
  Props as FieldPopperProps,
} from 'views/EMR/components/TextAreaToolbar/FieldPopper';
import PopulateLookup from 'core/components/Lookup';
import {
  getApiTemplateGetFieldList,
} from 'endpoints';
import {
  settingsEventChannel,
} from 'pages/Dashboard/services/eventChannels';

export default function FieldToolbarItem({
  editor,
  contentEditorRef,
  setData,
}: ToolbarItemProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fieldPopperParams, setFieldPopperParams] = useState<
    Partial<FieldPopperProps> | null>(null);

  const handleMouseMove = debounce((e: MouseEvent) => {
    const element = e.target as HTMLElement;
    if (element.classList.contains('__populate__-field')) {
      setFieldPopperParams({
        anchorElement: element,
        field: {
          templateFieldId: Number(element.dataset.fieldId ?? 0),
        },
        selected: JSON.parse(element.dataset.fieldSelection ?? '[]'),
      });
    }
  }, 50);

  const onPopperClose = () => {
    setFieldPopperParams(null);
  };

  useEffect(() => {
    contentEditorRef?.current?.addEventListener('mousemove', handleMouseMove);

    return () => {
      contentEditorRef?.current?.removeEventListener('mousemove', handleMouseMove);
    };
  }, [editor?.ui.isReady]);

  const addField = useCallback((field: TemplateFieldDTO) => {
    editor?.execute('field', field);

    setTimeout(() => {
      editor?.editing?.view?.focus?.();
      inputRef.current?.blur?.();
    }, 10);
  }, [editor]);

  return (
    <>
      <PopulateLookup<TemplateFieldDTO>
        inputRef={inputRef}
        value={null}
        type="field"
        onChange={(value) => {
          addField(value as TemplateFieldDTO);
        }}
        placeholder="Enter Field Name"
        getOptions={getApiTemplateGetFieldList}
        minSearchSize={0}
        defaultPageSize={500}
        clearOnBlur
        blurOnSelect
        openOnLoad={false}
        getUniqueId={(value) => value?.title?.toLowerCase() ?? ''}
        InputProps={{ autoFocus: false }}
        isOptionEqualToValue={(
          option,
          value,
        ) => option?.templateFieldId === (value as TemplateFieldDTO)?.templateFieldId}
        getLabel={(value) => value?.title ?? ''}
        onAddNew={async (value) => {
          settingsEventChannel.emit('onTemplateFieldDialog', {
            data: { title: value },
            onFieldCreated: (field) => {
              addField(field);
            },
          });
          return null;
        }}
        classes={{
          root: '!w-44 bg-white',
          inputRoot: '!py-0.5',
          endAdornment: 'test',
          listbox: '!max-h-[420px]',
        }}
        renderOptionRow={(option) => option?.title ?? '--'}
      />
      {!isNil(fieldPopperParams) && (
        <FieldPopper
          key={fieldPopperParams?.field?.templateFieldId}
          {...fieldPopperParams}
          onClose={onPopperClose}
          onChange={(selected = []) => {
            const { anchorElement, field } = fieldPopperParams;
            const { title } = field ?? {};
            const fieldSelection = JSON.stringify(selected);
            anchorElement!.dataset.fieldSelection = fieldSelection;
            anchorElement!.innerText = compact([title, selected.length > 0 ? fieldSelection : null]).join(' ');
            setData(contentEditorRef.current!.querySelector('#content')!.innerHTML ?? '');
          }}
          onRemove={() => {
            const { anchorElement } = fieldPopperParams;
            anchorElement?.remove();
            setData(contentEditorRef.current!.querySelector('#content')!.innerHTML ?? '');
            setFieldPopperParams(null);
          }}
        />
      )}
    </>
  );
}
